import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme context for managing color themes
const ThemeContext = createContext();

// Available themes
const themes = {
  default: 'default',
  grayscale: 'grayscale'
};

// Theme provider component
export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(themes.default);

  // Load saved theme preference from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('mlwb-theme');
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);
    
    // Force grayscale on external content when grayscale theme is active
    if (currentTheme === 'grayscale') {
      // Apply grayscale to all iframes and external content
      const applyGrayscaleToExternalContent = () => {
        const externalElements = document.querySelectorAll('iframe, embed, object, .map, .google-map, .leaflet-container, .mapboxgl-map, .ol-viewport, .esri-view, .map-container, .external-content, .third-party-widget, .embedded-content');
        externalElements.forEach(element => {
          element.style.filter = 'grayscale(100%)';
          element.style.webkitFilter = 'grayscale(100%)';
          element.style.mozFilter = 'grayscale(100%)';
          element.style.msFilter = 'grayscale(100%)';
          element.style.oFilter = 'grayscale(100%)';
        });
      };
      
      // Apply immediately
      applyGrayscaleToExternalContent();
      
      // Apply to dynamically loaded content
      const observer = new MutationObserver(applyGrayscaleToExternalContent);
      observer.observe(document.body, { childList: true, subtree: true });
      
      // Cleanup observer when theme changes
      return () => observer.disconnect();
    }
  }, [currentTheme]);

  // Function to toggle theme
  const toggleTheme = () => {
    const newTheme = currentTheme === themes.default ? themes.grayscale : themes.default;
    setCurrentTheme(newTheme);
    localStorage.setItem('mlwb-theme', newTheme);
  };

  // Function to set specific theme
  const setTheme = (theme) => {
    if (themes[theme]) {
      setCurrentTheme(theme);
      localStorage.setItem('mlwb-theme', theme);
    }
  };

  // Function to get current theme
  const getCurrentTheme = () => {
    return currentTheme;
  };

  // Function to check if current theme is grayscale
  const isGrayscale = () => {
    return currentTheme === themes.grayscale;
  };

  const value = {
    currentTheme,
    themes,
    toggleTheme,
    setTheme,
    getCurrentTheme,
    isGrayscale
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
